import { ReactFlowProvider } from 'reactflow';

import { ContextMenuProvider, PortalNodeContextProvider } from '@dbt-labs/sourdough';

import { DagProps, DbtDagNode } from '../dag/DagInterfaces';
import { DagContextMenuProvider } from '../providers/ContextMenuProvider';
import { DagProvider } from '../providers/DagProvider';
import { InternalDagProvider } from '../providers/InternalDagProvider';
import { ViewportProvider } from '../providers/ViewportProvider';
import { DagRouter } from './DagRouter';
import { LensLegend } from './LensLegend';
import { LensSwitcher } from './LensSwitcher';
import { ZoomControls } from './ZoomControls';

export const Dag = <TDbtDagNode extends DbtDagNode>({
  grain = 'project',
  toolbarItems,
  activeDbtCloudProject,
  padding,
  xOffset,
  yOffset,
  resetZoomOnResize,
  getContextMenuOptions,
  nodeType = 'lens',
  testId = 'dbt-dag',
  ...props
}: React.PropsWithChildren<DagProps<TDbtDagNode>>) => (
  <ContextMenuProvider>
    <PortalNodeContextProvider>
      <DagProvider
        overrideContext={false}
        options={{ disableMagnifiedNode: props.disableMagnifiedNode }}
      >
        <InternalDagProvider
          activeDbtCloudProject={activeDbtCloudProject}
          grain={grain}
          nodeType={nodeType}
          toolbarItems={toolbarItems}
        >
          <ReactFlowProvider>
            <div className="h-full w-full" data-testid={testId}>
              <ViewportProvider
                padding={padding}
                xOffset={xOffset}
                yOffset={yOffset}
                resetZoomOnResize={resetZoomOnResize}
                maxZoom={undefined /** props.maxZoom */}
                minZoom={props.minZoom}
              >
                <DagContextMenuProvider getContextMenuOptions={getContextMenuOptions}>
                  <DagRouter
                    {...props}
                    activeDbtCloudProject={activeDbtCloudProject}
                    grain={grain}
                  />
                </DagContextMenuProvider>
              </ViewportProvider>
            </div>
          </ReactFlowProvider>
        </InternalDagProvider>
      </DagProvider>
    </PortalNodeContextProvider>
  </ContextMenuProvider>
);

/**
 * Adds a legend for the active lens to the DAG
 */
Dag.LensLegend = LensLegend;

/**
 * Enables switching between lenses
 */
Dag.LensSwitcher = LensSwitcher;

/**
 * Adds a zoom-in and zoom-out button to the DAG
 */
Dag.ZoomControls = ZoomControls;
